import { useEffect, useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Combobox, Listbox, Icon, LegacyStack, Tag } from "@shopify/polaris";
import { SearchIcon } from "@shopify/polaris-icons";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetch, fetchById } from "../../services/user-select.service";
import useIsPhone from "features/files/use-is-phone";

function formatUserOption(row, showUserEmail) {
  const name = (row.name && String(row.name).trim()) || "";
  const email = (row.email && String(row.email).trim()) || "";
  const primary = name || email || String(row.id ?? "");
  let displayLabel = primary;
  if (showUserEmail && email && name && name !== email) {
    displayLabel = `${name} · ${email}`;
  } else if (showUserEmail && email && !name) {
    displayLabel = email;
  }
  return {
    value: row.id,
    label: primary,
    email: email || undefined,
    displayLabel,
  };
}

function UserSelect({
  value,
  onChange,
  isRequired,
  name,
  isDisable = false,
  label = "select_user",
  labelHidden = false,
  onlyActivated = false,
  showUserEmail = false,
}) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const [allOptions, setAllOptions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { companyId } = useParams();

  const displayOptions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return allOptions;
    return allOptions.filter((o) => {
      const inName = o.label.toLowerCase().includes(q);
      const inEmail =
        showUserEmail && (o.email || "").toLowerCase().includes(q);
      const inDisplay = (o.displayLabel || "")
        .toLowerCase()
        .includes(q);
      return inName || inEmail || inDisplay;
    });
  }, [allOptions, inputValue, showUserEmail]);

  const handleChange = useCallback(
    (selectedValue) => {
      if (isRequired && !selectedValue) {
        setError(t("field_is_required"));
      } else {
        setError("");
      }
      onChange(selectedValue, name);
    },
    [isRequired, name, onChange, t]
  );

  useEffect(() => {
    async function fetchData() {
      if (!companyId) return;
      try {
        setIsLoading(true);
        const data = await fetch(
          companyId,
          {},
          false,
          undefined,
          onlyActivated
        );
        const collection = data?.usersCollection ?? [];
        setAllOptions(collection.map((row) => formatUserOption(row, showUserEmail)));
      } catch (e) {
        console.error("Error fetching user collection:", e);
        setAllOptions([]);
      } finally {
        setIsLoading(false);
      }
    }

    fetchData();
  }, [companyId, onlyActivated, showUserEmail]);

  useEffect(() => {
    if (!value) {
      setSelectedOptions([]);
      return;
    }
    const found = allOptions.find((o) => o.value === value);
    if (found) {
      setSelectedOptions([found]);
      return;
    }
    if (!companyId || isLoading) return;

    let cancelled = false;
    (async () => {
      try {
        const { userEntity } = await fetchById(value, companyId);
        if (cancelled) return;
        const opt = formatUserOption(
          {
            id: userEntity.id,
            name: userEntity.name,
            email: userEntity.email,
          },
          showUserEmail
        );
        setSelectedOptions([opt]);
        setAllOptions((prev) =>
          prev.some((o) => o.value === opt.value) ? prev : [opt, ...prev]
        );
      } catch (e) {
        if (!cancelled) setSelectedOptions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [value, allOptions, companyId, isLoading, showUserEmail]);

  const handleInputChange = useCallback((input) => {
    setInputValue(input);
  }, []);

  const handleSelection = useCallback(
    (selected) => {
      const found = displayOptions.find((option) => option.value === selected);
      if (found) {
        setSelectedOptions([found]);
        setInputValue("");
        handleChange(selected);
      }
    },
    [displayOptions, handleChange]
  );

  const handleTagRemove = useCallback(() => {
    setSelectedOptions([]);
    setInputValue("");
    handleChange("");
  }, [handleChange]);

  const optionsMarkup = displayOptions.map((option) => {
    const shown = option.displayLabel || option.label;
    const a11y =
      showUserEmail &&
      option.email &&
      option.label &&
      option.label !== option.email
        ? `${option.label}, ${option.email}`
        : shown;
    return (
      <Listbox.Option
        key={option.value}
        value={option.value}
        selected={selectedOptions.some((o) => o.value === option.value)}
        accessibilityLabel={a11y}
      >
        {shown}
      </Listbox.Option>
    );
  });

  const verticalContentMarkup =
    selectedOptions.length > 0 ? (
      <LegacyStack spacing="extraTight" alignment="center">
        <Tag {...(!isDisable ? { onRemove: handleTagRemove } : {})}>
          {selectedOptions[0].displayLabel || selectedOptions[0].label}
        </Tag>
      </LegacyStack>
    ) : null;

  // The list of users opens under the field. On a phone the on-screen keyboard covers the lower half
  // of the screen, so the field is scrolled to the top to leave the list room above the keyboard.
  const scrollFieldToTop = (event) => {
    if (isPhone) event.target.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  return (
    <div className="user-select" onFocus={scrollFieldToTop}>
      <Combobox
        allowMultiple={false}
        activator={
          <Combobox.TextField
            prefix={<Icon source={SearchIcon} tone="base" />}
            onChange={handleInputChange}
            label={t(label)}
            labelHidden={labelHidden}
            value={inputValue}
            placeholder={
              t("user_search") !== "user_search"
                ? t("user_search")
                : "Search user..."
            }
            autoComplete="off"
            disabled={isDisable}
            requiredIndicator={isRequired}
            error={error}
            verticalContent={verticalContentMarkup}
          />
        }
      >
        <Listbox onSelect={handleSelection}>
          {isLoading ? (
            <Listbox.Loading accessibilityLabel="Loading" />
          ) : (
            <>{optionsMarkup}</>
          )}
        </Listbox>
      </Combobox>
    </div>
  );
}

export default UserSelect;

UserSelect.propTypes = {
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string,
  isRequired: PropTypes.bool,
  allowMultiple: PropTypes.bool,
  showUserEmail: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  labelHidden: PropTypes.bool,
};

UserSelect.defaultProps = {
  isRequired: false,
  showUserEmail: false,
};
